import React, { useCallback, useMemo } from 'react';
import { TYPE_CHART_TIMELINE, Chart, UiMessage } from 'stoerk-ui-components';
import _ from 'lodash';
import { STTimeLineChartData, TimeFrame } from '../../monitoring.model';
import { useTranslation } from 'react-i18next';

interface DigitalChannelsProps {
  data: STTimeLineChartData;
  internalTimeFrame: TimeFrame;
  width: number;
  height: number;
  labels: any;
  controls: any[];
  paletteColors: string[];
}

// eslint-disable-next-line react/display-name
export const DigitalChannels = React.memo((props: DigitalChannelsProps) => {
  const { data, internalTimeFrame, labels, width, height, paletteColors } =
    props;
  const i18n = useTranslation();
  const getColorPalette = useCallback(() => paletteColors, [paletteColors]);

  // Recalculate filtered data when internalTimeFrame changes, to avoid mutating the original data
  // and keep the data timeframe consistent with the zoomed view.
  const filteredData = useMemo(() => {
    const minTimestamp = internalTimeFrame.start.unix();
    const maxTimestamp = internalTimeFrame.end.unix();
    return Object.fromEntries(
      Object.entries(data).map(([key, series]) => [
        key,
        {
          ...series,
          data: series.data
            .filter(
              (range) =>
                range.end >= minTimestamp && range.start <= maxTimestamp
            )
            .map((range) => ({
              ...range,
              start: Math.max(range.start, minTimestamp),
              end: Math.min(range.end, maxTimestamp),
              fakeStart: range.start < minTimestamp ? true : range.fakeStart,
              fakeEnd: range.end > maxTimestamp ? true : range.fakeEnd,
            })),
        },
      ])
    ) as STTimeLineChartData;
  }, [data, internalTimeFrame]);

  const dataEmpty = _.isEmpty(data);
  if (dataEmpty)
    return (
      <UiMessage
        attached
        icon="warning"
        header=""
        content={i18n.t('device.monitoring.not_data_to_show_digital_channels')}
      />
    );

  return (
    <Chart
      data={filteredData}
      type={TYPE_CHART_TIMELINE}
      // mode={mode}
      height={height}
      width={width}
      minDatetime={internalTimeFrame.start.unix()}
      maxDatetime={internalTimeFrame.end.unix()}
      zoom={true}
      language={i18n.i18n.language}
      labels={labels}
      getColorPalette={getColorPalette}
    />
  );
});
